import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button, Dropdown } from 'react-bootstrap';
import { useDispatch, useSelector } from "react-redux";
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import './ProjectAllocationForm.css';

import { fetchActivityTypes } from '../../../redux/slices/timeBalanceSlice';
import { fetchPauseReasons } from '../../../redux/slices/pausesSlice';
import { fetchUserProjects } from '../../../redux/slices/projectsSlice';

ChartJS.register(ArcElement, Tooltip, Legend);

const ProjectAllocationForm = ({ row, onSave, isLocked }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isVirtualRow = String(row.id).startsWith('virtual-');
  const isActiveDay = !isVirtualRow && Boolean(row.clock_in) && !row.clock_out;
  const getDefaultCheckOut = () => {
    if (row.clock_out) return String(row.clock_out).substring(0, 5);
    return isActiveDay ? '' : '18:00';
  };
  const [isEditing, setIsEditing] = useState(!isVirtualRow);

  const dbProjects = useSelector((state) => state.projects?.userProjects[row.user_id] || []);
  const genericActivityTypesFromRedux = useSelector((state) => state.timeBalances?.activityTypes || []);
  const pauseReasons = useSelector((state) => state.pauses?.reasons || []);
  const loadingProjects = useSelector((state) => state.projects?.isLoading || false);

  const genericActivityTypes = genericActivityTypesFromRedux.filter(t => t !== 'Project');

  const defaultPauses = [{ start: '00:00', end: '00:00', reason: '' }];
  const normalizePauses = (raw) => {
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw.map((p) => ({
      start: p.start ? String(p.start).substring(0, 5) : '',
      end: p.end ? String(p.end).substring(0, 5) : '',
      reason: p.reason || '',
    }));
  };

  const [checkIn, setCheckIn] = useState(row.clock_in ? String(row.clock_in).substring(0, 5) : '09:00');
  const [checkOut, setCheckOut] = useState(getDefaultCheckOut);
  const [pauses, setPauses] = useState(() => {
    const normalized = normalizePauses(row.pauses);
    return normalized.length > 0 ? normalized : defaultPauses;
  });
  
  const [allocations, setAllocations] = useState([]);
  const [calculatedWorkedMinutes, setCalculatedWorkedMinutes] = useState(0);

  useEffect(() => {
    if (!isEditing) return;
    dispatch(fetchActivityTypes());
    dispatch(fetchPauseReasons());
    if (row.user_id) {
        dispatch(fetchUserProjects(row.user_id));
    }
  }, [dispatch, isEditing, row.user_id]);

  useEffect(() => {
    if (!row) return;

    const normalizedPauses = normalizePauses(row.pauses);
    setCheckIn(row.clock_in ? String(row.clock_in).substring(0, 5) : '09:00');
    setCheckOut(getDefaultCheckOut());
    setPauses(normalizedPauses.length > 0 ? normalizedPauses : defaultPauses);

    setAllocations(
      row.projects?.map((p) => ({
        id: p.project_id || p.type,
        name: p.name,
        type: p.type || 'Project',
        minutes: p.minutes_logged !== undefined ? p.minutes_logged : Math.round((p.hours_logged || 0) * 60),
        color: p.type === 'Project' ? (p.color || '#8bdb55') : '#b8c7db',
      })) || []
    );

    setIsEditing(!String(row.id).startsWith('virtual-'));
  }, [row.id]);

  const minutesToHM = (minutes) => {
    const minStr = parseInt(minutes) || 0;
    const h = Math.floor(minStr / 60);
    const m = minStr % 60;
    return `${h}h ${m}m`;
  };

  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hrs, mins] = timeStr.split(':').map(Number);
    return hrs * 60 + (mins || 0);
  };

  const getDurationInMinutes = (start, end) => {
    if (!start || !end) return 0;
    const startMins = timeToMinutes(start);
    const endMins = timeToMinutes(end);
    return endMins < startMins ? endMins + 1440 - startMins : endMins - startMins;
  };

  useEffect(() => {
    const totalBreakMinutes = pauses.reduce((sum, p) => sum + getDurationInMinutes(p.start, p.end), 0);
    const now = new Date();
    const shiftEnd = checkOut || (isActiveDay ? `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}` : '');
    const totalShiftMinutes = getDurationInMinutes(checkIn, shiftEnd);
    const netMinutes = totalShiftMinutes - totalBreakMinutes;
    setCalculatedWorkedMinutes(netMinutes > 0 ? netMinutes : 0);
  }, [checkIn, checkOut, pauses, isActiveDay]);

  const pieData = {
    labels: allocations.map((a) => a.name),
    datasets: [{
      data: allocations.map((a) => a.minutes || 0),
      backgroundColor: allocations.map((a) => a.color),
      borderWidth: 0,
    }],
  };


  const addPause = () => {
    if (pauses.length < 5) {
      setPauses([...pauses, { start: '', end: '', reason: '' }]);
    } else {
      toast.warning(t('components.ProjectAllocationForm.max_pauses', 'Limite máximo de 5 pausas atingido.'));
    }
  };

  const removePause = (index) => setPauses(pauses.filter((_, i) => i !== index));

  const updatePause = (index, field, value) => {
    setPauses((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddProject = (project) => {
    if (allocations.some((a) => String(a.id) === String(project.id))) return;
    setAllocations([...allocations, { id: project.id, name: project.name, type: 'Project', minutes: 0, color: project.color || '#5710B2' }]);
  };

  const handleAddGenericActivity = (type) => {
    if (allocations.some((a) => String(a.id) === String(type))) return;
    setAllocations([...allocations, { id: type, name: type, type: type, minutes: 0, color: '#64748b' }]);
  };

  const handleRemoveEntry = (id) => setAllocations(allocations.filter((a) => a.id !== id));

  const updateEntryMinutes = (id, totalMinutes) => {
    setAllocations(allocations.map((a) => (a.id === id ? { ...a, minutes: Math.max(0, totalMinutes) } : a)));
  };

  const currentAllocatedMinutes = allocations.reduce((sum, a) => sum + (a.minutes || 0), 0);
  const isAllocationMatching = isActiveDay && !checkOut
    ? currentAllocatedMinutes <= calculatedWorkedMinutes
    : calculatedWorkedMinutes === currentAllocatedMinutes;

  const handleFormSubmission = (e) => {
    e.preventDefault();

    const validPauses = pauses.filter((p) => p.start && p.end);
    const validAllocations = allocations.filter((a) => a.minutes > 0);

    const payload = {
      userId: row.user_id,
      date: row.date,
      clockData: { clock_in: checkIn, clock_out: checkOut },
      pauses: validPauses,
      projectAllocations: validAllocations.map((a) => ({
        project_id: a.type === 'Project' ? a.id : null,
        type: a.type,
        name: a.name,
        minutes: a.minutes,
      })),
    };

    onSave(payload);
  };


  const availableProjects = dbProjects.filter((p) => !allocations.some((a) => String(a.id) === String(p.id)));
  const availableGenerics = genericActivityTypes.filter((tk) => !allocations.some((a) => String(a.id) === String(tk)));

  if (!isEditing) {
    return (
      <div className="d-flex align-items-center justify-content-center py-2">
        <button
          type="button"
          className="btn-add-virtual-day"
          onClick={() => setIsEditing(true)}
        >
          + {t('components.ProjectAllocationForm.add_day', 'Adicionar dia')}
        </button>
      </div>
    );
  }

  return (
    <Form onSubmit={handleFormSubmission} className="split-audit-form">
      <Row className="align-items-start">
        <Col md={5} className="audit-section hours-card">
          <h6 className="section-title">{t('components.ProjectAllocationForm.hours_section')}</h6>
          <Row>
            <Col>
              <Form.Group className="mb-2">
                <Form.Label>{t('components.ProjectAllocationForm.check_in')}</Form.Label>
                <Form.Control type="time" disabled={isLocked} value={checkIn} onChange={(e) => setCheckIn(e.target.value)} />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-2">
                <Form.Label>{t('components.ProjectAllocationForm.check_out')}</Form.Label>
                <Form.Control type="time" disabled={isLocked} value={checkOut} onChange={(e) => setCheckOut(e.target.value)} />
                {isActiveDay && !checkOut && (
                  <Form.Text muted>{t('components.ProjectAllocationForm.shift_in_progress')}</Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>
          <hr />

          <h6 className="section-title">
            {t('components.ProjectAllocationForm.pauses')} ({pauses.length}/5)
            <Button variant="link" size="sm" onClick={addPause} disabled={isLocked || pauses.length >= 5}>
              {t('components.ProjectAllocationForm.add')}
            </Button>
          </h6>

          {pauses.map((p, index) => (
            <Row key={index} className="mb-1 align-items-center">
              <Col md={3}>
                <Form.Control type="time" size="sm" disabled={isLocked} value={p.start} onChange={(e) => updatePause(index, 'start', e.target.value)} />
              </Col>
              <Col md={3}>
                <Form.Control type="time" size="sm" disabled={isLocked} value={p.end} onChange={(e) => updatePause(index, 'end', e.target.value)} />
              </Col>
              <Col md={5}>
                <Form.Select
                  size="sm"
                  value={p.reason}
                  disabled={isLocked}
                  onChange={(e) => updatePause(index, 'reason', e.target.value)}
                >
                  <option value="">{t('components.ProjectAllocationForm.select_placeholder', 'Selecione...')}</option>
                  {pauseReasons.map((reason, i) => (
                    <option key={i} value={reason}>
                      {t(`components.ClockCard.reasons.${reason}`, reason)}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={1}>
                <Button variant="link" className="delete-btn m-0 p-2 text-danger" disabled={isLocked} onClick={() => removePause(index)}>-</Button>
              </Col>
            </Row>
          ))}
        </Col>

        <Col md={2} className="audit-divider-panel">
          <div className="calculation-badge">
            <span className="badge-label">{t('components.ProjectAllocationForm.calculated')}</span>
            <h5 className="badge-value fw-bold">{minutesToHM(calculatedWorkedMinutes)}</h5>
          </div>
          <div className="calculation-badge mb-4">
            <span className="badge-label">{t('components.ProjectAllocationForm.allocated')}</span>
            <h5 className={`badge-value fw-bold ${isAllocationMatching ? 'match-success' : 'match-error'}`}>
              {minutesToHM(currentAllocatedMinutes)}
            </h5>
          </div>
          <div className="audit-chart-wrapper" style={{ width: '100%', height: '120px' }}>
            <Pie data={pieData} options={{ maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
          </div>
        </Col>

        <Col md={5} className="audit-section projects-card">
          <h6 className="section-title">{t('components.ProjectAllocationForm.projects_section')}</h6>
          <Dropdown className="w-100 mb-3">
            <Dropdown.Toggle
              size="sm"
              className="w-100"
              disabled={isLocked || loadingProjects || (availableProjects.length === 0 && availableGenerics.length === 0)}
            >
              {loadingProjects ? t('components.ProjectAllocationForm.loading', 'A carregar...') : t('components.ProjectAllocationForm.add_placeholder')}
            </Dropdown.Toggle>
            <Dropdown.Menu className="w-100">
              {availableProjects.length === 0 && availableGenerics.length === 0 ? (
                <Dropdown.Item disabled>{t('components.ProjectAllocationForm.no_options', 'Nenhuma opção restante')}</Dropdown.Item>
              ) : (
                <>
                  {availableProjects.map((p) => (
                    <Dropdown.Item key={p.id} onClick={() => handleAddProject(p)}>{p.name}</Dropdown.Item>
                  ))}
                  {availableProjects.length > 0 && availableGenerics.length > 0 && <Dropdown.Divider />}
                  {availableGenerics.map((tk) => (
                    <Dropdown.Item key={tk} onClick={() => handleAddGenericActivity(tk)}>
                      {t(`components.ProjectAllocationForm.generic_types.${tk}`, tk)}
                    </Dropdown.Item>
                  ))}
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>

          <div className="allocated-projects-list">
            {allocations.map((proj) => (
              <div key={proj.id} className="project-row-item d-flex justify-content-between align-items-center mb-2">
                <span className="text-truncate" style={{ maxWidth: '40%' }}>{proj.name}</span>

                <div className="d-flex align-items-center gap-1 justify-content-end">
                  <Form.Control
                    type="number"
                    min="0"
                    disabled={isLocked}
                    value={Math.floor(proj.minutes / 60)}
                    onChange={(e) => {
                      const h = parseInt(e.target.value) || 0;
                      const m = proj.minutes % 60;
                      updateEntryMinutes(proj.id, h * 60 + m);
                    }}
                    className="form-control-sm text-center"
                    style={{ width: "55px" }}
                  />
                  <span className="small text-muted">h</span>

                  <Form.Control
                    type="number"
                    min="0"
                    max="59"
                    disabled={isLocked}
                    value={proj.minutes % 60}
                    onChange={(e) => {
                      const h = Math.floor(proj.minutes / 60);
                      const m = Math.min(59, parseInt(e.target.value) || 0);
                      updateEntryMinutes(proj.id, h * 60 + m);
                    }}
                    className="form-control-sm text-center"
                    style={{ width: "55px" }}
                  />
                  <span className="small text-muted">m</span>

                  <Button size="sm" variant="link" className="text-danger p-0 ms-2" disabled={isLocked} onClick={() => handleRemoveEntry(proj.id)}>×</Button>
                </div>
              </div>
            ))}
          </div>
        </Col>
      </Row>

      <div className="audit-form-footer text-end mt-3">
        {isVirtualRow && (
          <Button
            variant="link"
            size="sm"
            className="text-secondary me-2"
            onClick={() => setIsEditing(false)}
          >
            {t('components.ProjectAllocationForm.cancel', 'Cancelar')}
          </Button>
        )}
        <Button type="submit" disabled={isLocked || !isAllocationMatching}>
          {t('components.ProjectAllocationForm.save_button')}
        </Button>
      </div>
    </Form>
  );
};

export default ProjectAllocationForm;

